import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ProgressBar from "components/ProgressBar";
import Button from "components/Button";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import InputMask from "react-input-mask";
import { usePlacesWidget } from "react-google-autocomplete";
import { useMutation, useLazyQuery } from "@apollo/client";
import { CREATE_IDENTITY_SCAN, UPDATE_IDENTITY_SCAN, GET_GROUP_DETAILS_BY_GROUP_ID } from "graphql/service";
import {
  Head,
  Container,
  Content,
  Row,
  StatusContainer,
  Footer,
} from "./styles";
import moment from "moment-timezone";
import { update } from "lodash-es";

const CustomerIdentification = ({ status }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const groupId = useParams().groupId;
  const scanResult = location.state?.scanResult;
  const [getGroupDetailsByGroupId,{ data: groupData }] = useLazyQuery(GET_GROUP_DETAILS_BY_GROUP_ID, { variables: { groupId } });
  const scan = scanResult || groupData?.groupByID?.identityScans?.[0];
  const [createIdentityScan, { loading: createLoading }] = useMutation(
    CREATE_IDENTITY_SCAN
  );

  const [updateIdentityScan, { loading: updateLoading }] = useMutation(
    UPDATE_IDENTITY_SCAN
  );

  const [guestInfo, setGuestInfo] = useState({
    id: "",
    fullName: "",
    dateOfBirth: "",
    address: "",
    documentNumber: "",
    documentType: "",
    dateOfIssue: "",
    dateOfExpiry: "",
    issuingState: "",
    nationality: "",
  });

  useEffect(() => {
    if(!scanResult) {
      getGroupDetailsByGroupId();
    }
  }, [scanResult, getGroupDetailsByGroupId]);

  useEffect(() => {
    if (scan) {
      setGuestInfo({
        fullName: scan.fullName || "N/A",
        dateOfBirth: scan.dateOfBirth? moment(scan.dateOfBirth).format("MM/DD/YYYY"): "N/A",
        address: scan.address || "N/A",
        documentNumber: scan.documentNumber || "N/A",
        documentType: scan.documentType || "N/A",
        dateOfIssue: scan.dateOfIssue? moment(scan.dateOfIssue).format("MM/DD/YYYY"): "N/A",
        dateOfExpiry: scan.dateOfExpiry? moment(scan.dateOfExpiry).format("MM/DD/YYYY"): "N/A",
        issuingState: scan.issuingState || "N/A",
        nationality: scan.nationality || "N/A",
      });
    }
  }, [scan]);

  const { ref } = usePlacesWidget({
    apiKey: `${process.env.REACT_APP_GOOGLE_APIKEY}&loading=async`,
    onPlaceSelected: (place) => {
      const components = place.address_components || [];

      const getComponent = (type) =>
        components.find((component) => component.types.includes(type))
          ?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      const streetAddressLine2 = ""; // Google Autocomplete doesn't return this
      const city =
        getComponent("locality") || getComponent("sublocality") || "";
      const province = getComponent("administrative_area_level_1");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");
      setGuestInfo({
        ...guestInfo,
        address:
          streetAddress +
          ", " +
          streetAddressLine2 +
          ", " +
          city +
          ", " +
          province +
          ", " +
          postalCode +
          ", " +
          country,
      });
    },
    options: {
      types: ["address"],
    },
  });

  const documentTypeOptions = [
    { label: "Passport", value: "Passport" },
    { label: "Driver's License", value: "Driver's License" },
  ];

  const handleContinue = () => {
    const updatedGuestInfo = {
      fullName: guestInfo.fullName,
      address: guestInfo.address,
      documentNumber: guestInfo.documentNumber,
      documentType: guestInfo.documentType,
      issuingState: guestInfo.issuingState,
      nationality: guestInfo.nationality
    }
    if(guestInfo.dateOfBirth !== "N/A" && guestInfo.dateOfBirth !== "" && guestInfo.dateOfBirth !== null) {
      updatedGuestInfo["dateOfBirth"] = moment(guestInfo.dateOfBirth, "MM/DD/YYYY").toDate()
    }
    if(guestInfo.dateOfIssue !== "N/A" && guestInfo.dateOfIssue !== "" && guestInfo.dateOfIssue !== null) {
      updatedGuestInfo["dateOfIssue"] = moment(guestInfo.dateOfIssue, "MM/DD/YYYY").toDate()
    }
    if(guestInfo.dateOfExpiry !== "N/A" && guestInfo.dateOfExpiry !== "" && guestInfo.dateOfExpiry !== null) {
      updatedGuestInfo["dateOfExpiry"] = moment(guestInfo.dateOfExpiry, "MM/DD/YYYY").toDate()
    }

    if (guestInfo.id) {
      updatedGuestInfo["id"] = guestInfo.id
      updateIdentityScan({
        variables: {
          input: updatedGuestInfo
        },
      });
    } else {
      updatedGuestInfo["groupId"] = groupId
      createIdentityScan({
        variables: {
          input: updatedGuestInfo
        },
      });
    }
    navigate(`/group-check-in/payment/${groupId}`);
  };

  return (
    <Container>
      <ProgressBar step={2} groupId={groupId} status={status} />
      <Content>
        <Head>Customer Identification</Head>
        <Row>
          <Input
            title="NAME"
            value={guestInfo.fullName}
            onChange={(e) => {
              setGuestInfo((guestInfo) => ({
                ...guestInfo,
                fullName: e.target.value,
              }));
            }}
            additionalstyle={`
                flex-basis: 100%;
              `}
          />
          <InputMask
            mask={guestInfo.dateOfBirth=== "N/A"? "" :[
              /[0-1]/,
              /[0-9]/,
              "/",
              /[0-3]/,
              /[0-9]/,
              "/",
              /[1-2]/,
              /[0-9]/,
              /[0-9]/,
              /[0-9]/,
            ]}
            maskPlaceholder="__/__/____"
            value={guestInfo.dateOfBirth}
            onChange={(e) => {
              setGuestInfo((guestInfo) => ({
                ...guestInfo,
                dateOfBirth: e.target.value,
              }));
            }}
          >
            <Input
              title="DOB (MM/DD/YYYY)"
              additionalstyle={`
                  flex-basis: 100%;
                `}
            />
          </InputMask>
        </Row>
        {guestInfo.documentType !== "Passport" && (
          <Row>
            <Input
              inputFieldRef={ref}
              title="ADDRESS"
              value={guestInfo.address}
              onChange={(e) => {
                setGuestInfo((guestInfo) => ({
                  ...guestInfo,
                  address: e.target.value,
                }));
              }}
              additionalstyle={`
                flex-basis: 100%;
              `}
            />
          </Row>
        )}
        <Row>
          <Input
            title="TRAVEL DOCUMENT #"
            value={guestInfo.documentNumber}
            onChange={(e) => {
              setGuestInfo((guestInfo) => ({
                ...guestInfo,
                documentNumber: e.target.value,
              }));
            }}
            additionalstyle={`
                flex-basis: 100%;
              `}
          />
          <Dropdown
            title="TRAVEL DOCUMENT TYPE"
            options={documentTypeOptions}
            value={
              documentTypeOptions.find(
                (option) => option.value === guestInfo.documentType
              )?.label
            }
            onChange={(e) => {
              setGuestInfo((guestInfo) => ({
                ...guestInfo,
                documentType: e,
              }));
            }}
          />
        </Row>
        <Row>
          <InputMask
            mask={guestInfo.dateOfIssue==="N/A"? "": [
              /[0-1]/,
              /[0-9]/,
              "/",
              /[0-3]/,
              /[0-9]/,
              "/",
              /[1-2]/,
              /[0-9]/,
              /[0-9]/,
              /[0-9]/,
            ]}
            maskPlaceholder="__/__/____"
            value={guestInfo.dateOfIssue}
            onChange={(e) => {
              setGuestInfo((guestInfo) => ({
                ...guestInfo,
                dateOfIssue: e.target.value,
              }));
            }}
          >
            <Input
              title="DATE OF ISSUE"
              additionalstyle={`
                  flex-basis: 100%;
                `}
            />
          </InputMask>
          <InputMask
            mask={guestInfo.dateOfExpiry==="N/A"? "": [
              /[0-1]/,
              /[0-9]/,
              "/",
              /[0-3]/,
              /[0-9]/,
              "/",
              /[1-2]/,
              /[0-9]/,
              /[0-9]/,
              /[0-9]/,
            ]}
            maskPlaceholder="__/__/____"
            value={guestInfo.dateOfExpiry}
            onChange={(e) => {
              setGuestInfo((guestInfo) => ({
                ...guestInfo,
                dateOfExpiry: e.target.value,
              }));
            }}
          >
            <Input
              title="DOCUMENT EXPIRY"
              additionalstyle={`
                  flex-basis: 100%;
                `}
            />
          </InputMask>
        </Row>
        <Row>
          <Input
            title="PROVINCE/STATE"
            value={guestInfo.issuingState}
            onChange={(e) => {
              setGuestInfo((guestInfo) => ({
                ...guestInfo,
                issuingState: e.target.value,
              }));
            }}
            additionalstyle={`
                flex-basis: 100%;
              `}
          />          
          <Input
            title="COUNTRY"
            value={guestInfo.nationality}
            onChange={(e) => {
              setGuestInfo((guestInfo) => ({
                ...guestInfo,
                nationality: e.target.value,
              }));
            }}
            additionalstyle={`
                flex-basis: 100%;
              `}
          />
        </Row>
        {scan?.statusChecks && (
          <>
            <Head additionalstyle={"margin-top: 48px;"}>
              Document Validation
            </Head>
            <StatusContainer>
              <div className="overall">
                <div className="statusTitle">
                  <span
                    className={
                      "statusDot large " +
                      (scan.statusChecks.overallPassed ? "success" : "failed")
                    }
                  ></span>
                  <span className="status">OVERALL RESULT</span>
                </div>
              </div>
              <div className="details">
                <div className="statusTitle">
                  <span
                    className={
                      "statusDot " +
                      (scan.statusChecks.documentRecognized
                        ? "success"
                        : "failed")
                    }
                  ></span>
                  <span className="status">DOCUMENT VALID</span>
                </div>
                <div className="statusTitle">
                  <span
                    className={
                      "statusDot " +
                      (scan.statusChecks.isExpired === false
                        ? "success"
                        : "failed")
                    }
                  ></span>
                  <span className="status">NOT EXPIRED</span>
                </div>
                <div className="statusTitle">
                  <span
                    className={
                      "statusDot " +
                      (scan.statusChecks.securityPassed
                        ? "success"
                        : "failed")
                    }
                  ></span>
                  <span className="status">DOCUMENT SECURITY</span>
                </div>
                <div className="statusTitle">
                  <span
                    className={
                      "statusDot " + (scan.age >= 18 ? "success" : "failed")
                    }
                  ></span>
                  <span className="status">ADULT</span>
                </div>
              </div>
            </StatusContainer>
          </>
        )}
        <Footer>
          <div className="buttonContainer">
            <Button
              success
              additionalstyle={`
                width: 97px;
              `}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </div>
        </Footer>
      </Content>
    </Container>
  );
};

export default CustomerIdentification;
